import {
  BORDER_RADIUS,
  BORDER_RADIUS_SMALL,
  boxShadow,
  dividerPrimary,
  label,
  lightGreen,
  lightRed,
  primary,
  secondary,
  TRANSITION_BACKGROUND,
  white,
} from 'styles'
import { hexToRGBA } from 'utils'

import { makeStyles } from '@mui/styles'

import { DetailsType } from './constants'

interface Props {
  detailsType?: DetailsType
  isPlanCompleted?: boolean
  percent?: number | null
}

export default makeStyles({
  accordion: {
    '&:first-child': {
      borderRadius: '0.7rem',
    },
    '&:last-child': {
      borderRadius: '0.7rem',
    },
    '&&': {
      boxShadow,
    },
  },
  accordionDetails: {
    '&&': {
      backgroundColor: '#F5F7F8',
      borderTop: `1px solid ${label}`,
      padding: '0',
    },
  },
  accordionSummaryContent: {
    '&&': {
      display: 'block',
      margin: '0',
      padding: '0.5rem 0',
    },
  },
  accordionSummaryExpanded: {
    '&&&': {
      margin: '0',
    },
  },
  accordionSummaryRoot: {
    position: 'relative',
  },
  avg: {
    fontWeight: '700',
  },
  channelName: {
    alignItems: 'center',
    color: secondary,
    display: 'flex',
    fontWeight: 'bold',
    height: '5.75rem',
    maxHeight: '5.75rem',
    textTransform: 'uppercase',
  },
  channelSeparator: {
    backgroundColor: dividerPrimary,
    borderRadius: '1rem',
    margin: '0.2rem 0.5rem',
    width: '0.1rem',
  },
  detailsType: {
    '& > div': {
      alignItems: 'center',
      backgroundColor: '#DFDFDF',
      borderRadius: BORDER_RADIUS_SMALL,
      display: 'flex',
      fontWeight: '600',
      justifyContent: 'center',
      minHeight: '1.75rem',
      minWidth: '5rem',
      position: 'relative',
      textTransform: 'uppercase',
    },
    display: 'flex',
    justifyContent: 'center',
    marginTop: '0.25rem',
    '& > div:after': {
      backgroundColor: hexToRGBA('#72BD00', 0.4),
      borderRadius: BORDER_RADIUS_SMALL,
      content: '" "',
      display: ({ detailsType }: Props = {}) => (detailsType === DetailsType.BUDGET ? 'block' : 'none'),
      height: '100%',
      left: '0',
      position: 'absolute',
      top: '0',
      width: ({ percent }: Props = {}) => `${Math.min(100, (percent || 0) * 100)}%`,
    },
  },
  disabled: {
    '&&': {
      '&.Mui-disabled': {
        backgroundColor: white,
      },
    },
  },
  dropdownIcon: {
    bottom: '0.5rem',
    color: primary,
    position: 'absolute',
    right: '0',
  },
  ecom: {
    display: 'inline-block',
  },
  ecomShare: {
    width: '2.5rem',
  },
  flag: {
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    height: '2rem',
    marginRight: '0.5rem',
    width: '2rem',
  },
  item: {
    marginTop: '0.5rem',
  },
  label: {
    color: label,
    display: 'inline-block',
    textAlign: 'center',
    width: '2rem',
  },
  labelText: {
    color: label,
    display: 'inline-block',
    fontSize: '0.5rem',
    height: '1.5rem',
    lineHeight: '1.5rem',
    textAlign: 'center',
  },
  lastYear: {
    height: '1.25rem',
    marginRight: '0',
  },
  loader: {
    flexBasis: '67%',
  },
  logo: {
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    height: '100%',
    maxHeight: '3rem',
    width: '100%',
  },
  marginComparedValue: {
    '&:before': {
      content: '"|"',
      marginRight: '0.25rem',
    },
    whiteSpace: 'nowrap',
  },
  marginPercent: {
    display: 'inline-block',
    width: '2.5rem',
  },
  marginValue: {
    '&:before': {
      content: '"|"',
      marginRight: '0.25rem',
    },
    display: 'inline-block',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
  },
  orderChecked: {
    fontSize: '1.75rem',
  },
  percent: {
    '&&': {
      display: 'inline-block',
      fontSize: '1rem',
      marginLeft: '0.25rem',
    },
  },
  revenue: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
  },
  revenuePlan: {
    marginLeft: '0.2rem',
    color: ({ isPlanCompleted }: Props = {}) => (isPlanCompleted ? lightGreen : lightRed),

    '&& .info_percent-difference': {
      color: ({ isPlanCompleted }: Props = {}) => (isPlanCompleted ? lightGreen : lightRed),
    },
  },
  root: {
    padding: '0.5rem 1.5rem',
    position: 'relative',
  },
  rootDetailed: {
    padding: '0.5rem',
    position: 'relative',
  },
  row1: {
    alignItems: 'baseline',
    borderBottom: `1px solid ${hexToRGBA(primary, 0.05)}`,
  },
  row2: {
    height: '1.75rem',
    lineHeight: '1.5rem',
    maxHeight: '1.75rem',
    whiteSpace: 'nowrap',
  },
  row3: {
    '&&': {
      alignItems: 'flex-end',
      borderTop: `1px solid ${hexToRGBA(primary, 0.05)}`,
      marginBottom: '0.5rem',
    },
  },
  row4: {
    height: '1.75rem',
    lineHeight: '1.5rem',
    maxHeight: '1.75rem',
    whiteSpace: 'nowrap',
  },
  separator: {
    '&:before': {
      content: '"|"',
      marginLeft: '0.15rem',
      marginRight: '0.15rem',
    },
    whiteSpace: 'nowrap',
  },
  shareCompared: {
    color: '#989898',
  },
  sliderDots: {
    position: 'absolute',
    right: '0',
    top: '0',
  },
  storesCountIcon: {
    color: label,
    fontSize: '1rem',
  },
  subList: {
    '&&': {
      backgroundColor: '#E8E8E8',
    },
  },
  swipeFirst: {
    backgroundColor: white,
    borderBottomRightRadius: BORDER_RADIUS,
    borderTopRightRadius: BORDER_RADIUS,
    boxShadow,
    content: '" "',
    display: 'block',
    height: 'calc(100% + 1rem)',
    position: 'absolute',
    right: 'calc(100% + 1rem)',
    top: '-0.5rem',
    transition: TRANSITION_BACKGROUND,
    width: '1rem',
  },
  swipeFirstInactive: {
    '&&': {
      backgroundColor: 'transparent',
      boxShadow: 'none',
      cursor: 'initial',
    },
  },
  swipeLast: {
    backgroundColor: white,
    borderBottomLeftRadius: BORDER_RADIUS,
    borderTopLeftRadius: BORDER_RADIUS,
    boxShadow,
    content: '" "',
    display: 'block',
    height: 'calc(100% + 1rem)',
    left: 'calc(100% + 1rem)',
    position: 'absolute',
    top: '-0.5rem',
    transition: TRANSITION_BACKGROUND,
    width: '1rem',
  },
  swipeLastInactive: {
    '&&': {
      backgroundColor: 'transparent',
      boxShadow: 'none',
      cursor: 'initial',
    },
  },
  title: {
    backgroundColor: hexToRGBA(primary, 0.05),
    borderRadius: BORDER_RADIUS,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    justifyContent: 'space-between',
    marginRight: '0.3rem',
    minHeight: '8.5rem',
    padding: '1rem 0.5rem 0.5rem 0.5rem',
  },
})
